import { z } from 'zod';
import { APPOINTMENT_STATUS, ESTIMATE_STATUS, JOB_PRIORITY, JOB_STATUS } from '../enums.js';
import {
  EntityMetaSchema, LineItemSchema, zCents, zEnum, zNonEmpty, zPercent,
} from './common.js';

/** A Good/Better/Best (or single) option group within an estimate. */
export const EstimateOptionSchema = z.object({
  id: z.string(),
  name: zNonEmpty.max(80), // "Good" / "Better" / "Best" / "Standard"
  description: z.string().max(2000).optional(),
  lineItems: z.array(LineItemSchema).default([]),
  recommended: z.boolean().default(false),
});
export type EstimateOption = z.infer<typeof EstimateOptionSchema>;

export const EstimateSchema = EntityMetaSchema.extend({
  number: z.string(), // human-facing sequential-ish number, tenant-scoped
  customerId: z.string(),
  locationId: z.string().optional(),
  jobId: z.string().optional(),
  status: zEnum(ESTIMATE_STATUS).default('draft'),
  title: z.string().max(160).optional(),
  options: z.array(EstimateOptionSchema).min(1),
  selectedOptionId: z.string().optional(), // set on approval
  taxRate: zPercent.default(0),
  depositCents: z.number().int().min(0).default(0),
  expiresOn: z.string().optional(), // ISO date
  terms: z.string().max(8000).optional(),
  photoKeys: z.array(z.string()).default([]),
  signatureKey: z.string().optional(), // S3 key of captured signature image
  // Lifecycle timestamps
  sentAt: z.string().optional(),
  viewedAt: z.string().optional(),
  approvedAt: z.string().optional(),
  declinedAt: z.string().optional(),
  // Server-computed totals (cents), stored for fast reads:
  subtotalCents: z.number().int().default(0),
  taxCents: z.number().int().default(0),
  totalCents: z.number().int().default(0),
});
export type Estimate = z.infer<typeof EstimateSchema>;

export const CreateEstimateSchema = z.object({
  customerId: zNonEmpty,
  locationId: z.string().optional(),
  title: z.string().max(160).optional(),
  options: z.array(EstimateOptionSchema.omit({ id: true })).min(1, 'Add at least one option'),
  taxRate: zPercent.optional(),
  depositCents: z.number().int().min(0).optional(),
  expiresOn: z.string().optional(),
  terms: z.string().max(8000).optional(),
});
export type CreateEstimateInput = z.infer<typeof CreateEstimateSchema>;
export const UpdateEstimateSchema = CreateEstimateSchema.partial().omit({ customerId: true });
export type UpdateEstimateInput = z.infer<typeof UpdateEstimateSchema>;

/** Customer-portal approval payload. */
export const ApproveEstimateSchema = z.object({
  selectedOptionId: zNonEmpty,
  signatureDataUrl: z.string().optional(), // base64 image captured in portal; server stores to S3
});
export type ApproveEstimateInput = z.infer<typeof ApproveEstimateSchema>;

/** Materials used, recorded on a job (often by a tech in the field, offline-capable). */
export const JobMaterialSchema = z.object({
  id: z.string(),
  pricebookItemId: z.string().optional(),
  name: zNonEmpty.max(160),
  quantity: z.number().positive().default(1),
  unitPriceCents: zCents,
  unitCostCents: z.number().int().min(0).optional(),
});
export type JobMaterial = z.infer<typeof JobMaterialSchema>;

export const ChecklistItemSchema = z.object({
  id: z.string(),
  label: zNonEmpty.max(200),
  done: z.boolean().default(false),
  doneAt: z.string().optional(),
});
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

/** A cost/expense logged against a job (Phase II P2-M1). */
export const JobExpenseSchema = z.object({
  id: z.string(),
  description: zNonEmpty.max(200),
  category: zEnum(['materials', 'labor', 'permit', 'subcontractor', 'equipment_rental', 'fuel', 'other']),
  amountCents: z.number().int().min(0),
  vendorName: z.string().max(120).optional(),
  receiptDocId: z.string().optional(),
  billable: z.boolean().default(false),
});
export type JobExpense = z.infer<typeof JobExpenseSchema>;

export const JobSchema = EntityMetaSchema.extend({
  number: z.string(),
  customerId: z.string(),
  locationId: z.string().optional(),
  estimateId: z.string().optional(),
  invoiceId: z.string().optional(),
  type: z.string().max(80).optional(), // from tenant jobTypes
  priority: zEnum(JOB_PRIORITY).default('normal'),
  status: zEnum(JOB_STATUS).default('needs_scheduling'),
  title: zNonEmpty.max(160),
  description: z.string().max(8000).optional(),
  assignedUserIds: z.array(z.string()).default([]),
  checklist: z.array(ChecklistItemSchema).default([]),
  materials: z.array(JobMaterialSchema).default([]),
  photoKeys: z.array(z.string()).default([]),
  documentIds: z.array(z.string()).default([]),
  equipmentIds: z.array(z.string()).default([]),
  signatureKey: z.string().optional(),
  internalNotes: z.string().max(8000).optional(),
  expenses: z.array(JobExpenseSchema).default([]),
  propertyId: z.string().optional(),
  opportunityId: z.string().optional(),
  customFields: z.record(z.string(), z.string()).default({}),
  completedAt: z.string().optional(),
  canceledReason: z.string().max(500).optional(),
});
export type Job = z.infer<typeof JobSchema>;

export const CreateJobSchema = z.object({
  customerId: zNonEmpty,
  locationId: z.string().optional(),
  propertyId: z.string().optional(),
  opportunityId: z.string().optional(),
  estimateId: z.string().optional(),
  type: z.string().max(80).optional(),
  priority: zEnum(JOB_PRIORITY).optional(),
  title: zNonEmpty.max(160),
  description: z.string().max(8000).optional(),
  assignedUserIds: z.array(z.string()).optional(),
  checklist: z.array(ChecklistItemSchema.omit({ id: true, done: true, doneAt: true })).optional(),
  customFields: z.record(z.string(), z.string()).optional(),
});
export type CreateJobInput = z.infer<typeof CreateJobSchema>;
export const UpdateJobSchema = CreateJobSchema.partial().omit({ customerId: true });
export type UpdateJobInput = z.infer<typeof UpdateJobSchema>;

export const ChangeJobStatusSchema = z.object({
  status: zEnum(JOB_STATUS),
  reason: z.string().max(500).optional(),
});
export type ChangeJobStatusInput = z.infer<typeof ChangeJobStatusSchema>;

/** A scheduled visit. A job can have multiple appointments (multi-day / follow-ups). */
export const AppointmentSchema = EntityMetaSchema.extend({
  jobId: z.string(),
  customerId: z.string(),
  title: zNonEmpty.max(160),
  status: zEnum(APPOINTMENT_STATUS).default('scheduled'),
  start: z.string(), // ISO timestamp
  end: z.string(), // ISO timestamp
  assignedUserIds: z.array(z.string()).default([]),
  notes: z.string().max(2000).optional(),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const CreateAppointmentSchema = z.object({
  jobId: zNonEmpty,
  title: z.string().max(160).optional(),
  start: z.string().datetime({ message: 'Enter a valid start time' }),
  end: z.string().datetime({ message: 'Enter a valid end time' }),
  assignedUserIds: z.array(z.string()).optional(),
  notes: z.string().max(2000).optional(),
}).refine((v) => new Date(v.end) > new Date(v.start), {
  message: 'End time must be after start time',
  path: ['end'],
});
export type CreateAppointmentInput = z.infer<typeof CreateAppointmentSchema>;

export const MoveAppointmentSchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  assignedUserIds: z.array(z.string()).optional(),
}).refine((v) => new Date(v.end) > new Date(v.start), {
  message: 'End time must be after start time',
  path: ['end'],
});
export type MoveAppointmentInput = z.infer<typeof MoveAppointmentSchema>;
